import { useCallback, useEffect, useState } from "react";
import api from "../services/api";

const SEARCH_SETTLE_MS = 350;
const RESIZE_SETTLE_MS = 300;

/** The shared envelope, and the query params that produce it. */
const DEFAULT_QUERY_NAMES = {
  page: "page",
  limit: "limit",
  search: "search",
  sort: "sort",
  dir: "dir",
};

const defaultSelect = (data) => ({
  items: data?.items,
  total: data?.total,
  totalPages: data?.totalPages,
});

/** The value once it has stopped changing for `ms`. */
function useSettled(value, ms) {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

/**
 * A table whose rows live on the server.
 *
 * Owns the four things that decide which rows to show — page, limit, search
 * and sort — and refetches whenever any of them settles. The server does the
 * searching and sorting because the client only ever holds one page: filtering
 * or ordering here would quietly apply to that page alone.
 *
 * Both inputs that can change rapidly are debounced, for different reasons:
 * `search` because it changes per keystroke, and `limit` because it follows
 * the window height (see useFitRows) and would otherwise fire a request per
 * frame of a resize drag.
 *
 * Expects the shared envelope: { items, total, page, totalPages }.
 *
 * @param {string}  url        endpoint path, e.g. "/copier/copiers"
 * @param {number}  limit      rows per page (usually from useFitRows)
 * @param {string}  search     current search text, undebounced
 * @param {object}  sort       { col, dir } — col null means server default
 * @param {object}  params     extra query params (tab filters, ranges, …)
 * @param {boolean} enabled    false skips fetching entirely
 * @param {number}  refreshKey bump to force a refetch of the current page
 * @param {object}  query      names of the paging query params, for endpoints
 *                             that already speak a different dialect
 * @param {func}    select     pulls {items,total,totalPages} out of a response
 *                             that isn't in the shared envelope
 * @param {boolean} paged      false omits page/limit, asking for the whole
 *                             list — search and sort still run server-side
 */
export default function useServerTable({
  url,
  limit,
  search = "",
  sort = null,
  params = null,
  enabled = true,
  refreshKey = 0,
  query: queryNames = null,
  select = defaultSelect,
  paged = true,
}) {
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [data, setData] = useState(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState("");

  const names = { ...DEFAULT_QUERY_NAMES, ...(queryNames || {}) };
  const namesKey = JSON.stringify(names);

  const settledSearch = useSettled(search, SEARCH_SETTLE_MS);
  const settledLimit = useSettled(limit, RESIZE_SETTLE_MS);

  // Extra params are usually an inline object literal, so compare by value —
  // by identity every render would look like a change.
  const paramsKey = JSON.stringify(params ?? {});

  /* A new filter, ordering or page size makes the current page number
     meaningless. Reset during render rather than in an effect: an effect
     would let one request go out against the stale page first. */
  const filterKey = [settledSearch, sort?.col, sort?.dir, settledLimit, paramsKey, paged].join("|");
  const [lastFilterKey, setLastFilterKey] = useState(filterKey);
  if (lastFilterKey !== filterKey) {
    setLastFilterKey(filterKey);
    setPage(1);
  }

  const fetchRows = useCallback(() => {
    if (!enabled) return undefined;
    let active = true;
    setLoading(true);

    const q = JSON.parse(namesKey);
    const query = { ...JSON.parse(paramsKey) };
    if (paged) {
      query[q.page] = page;
      query[q.limit] = settledLimit;
    }
    if (settledSearch) query[q.search] = settledSearch;
    if (sort?.col) {
      query[q.sort] = sort.col;
      query[q.dir] = sort.dir;
    }

    api
      .get(url, { params: query })
      .then((res) => {
        if (!active) return;
        const picked = select(res.data);
        const pages = Math.max(1, picked.totalPages ?? 1);
        setRows(Array.isArray(picked.items) ? picked.items : []);
        setData(res.data);
        setTotal(picked.total ?? 0);
        setTotalPages(pages);
        setError("");
        // Rows can be deleted, or the page size can grow, while we're sitting
        // past the end of the list. Step back rather than show a blank table.
        if (page > pages) setPage(pages);
      })
      .catch(() => {
        if (active) setError("Failed to load");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, page, settledLimit, settledSearch, sort?.col, sort?.dir, paramsKey, namesKey, enabled, paged]);

  useEffect(() => fetchRows(), [fetchRows, refreshKey]);

  return { rows, data, total, page, setPage, totalPages, loading, error, refetch: fetchRows };
}
